import { MatchWishlistItemSchema, WishlistItemSchema } from '#src/wishlist_contracts.ts';
import { z } from 'zod';

// Fields

export const GroupIdSchema = z.string().trim().nonempty().max(24);
export const GroupNameSchema = z.string().trim().nonempty().max(255);

export const GroupParticipantIdSchema = z.string().trim().nonempty().max(24);
export const GroupParticipantNameSchema = z.string().trim().nonempty().max(255);

// POST /group

export const NewGroupParticipantSchema = z.object({
  name: GroupParticipantNameSchema,
  excludedNames: z.array(GroupParticipantNameSchema).max(148),
});

export const CreateGroupRequestSchema = z.object({
  name: GroupNameSchema,
  participants: z.array(NewGroupParticipantSchema).min(3).max(150),
});

export const CreateGroupSuccessResponseSchema = z.object({
  success: z.literal(true),
  id: GroupIdSchema,
});

export const CreateGroupErrorCodes = {
  INVALID_INPUT: 'INVALID_INPUT',
  MATCHING_FAILED: 'MATCHING_FAILED',
} as const;
export const CreateGroupErrorCodeSchema = z.enum(CreateGroupErrorCodes);
export const CreateGroupErrorResponseSchema = z.object({
  success: z.literal(false),
  code: CreateGroupErrorCodeSchema,
});

export const CreateGroupResponseSchema = z.discriminatedUnion('success', [
  CreateGroupSuccessResponseSchema,
  CreateGroupErrorResponseSchema,
]);

export type NewGroupParticipant = z.infer<typeof NewGroupParticipantSchema>;
export type CreateGroupRequest = z.infer<typeof CreateGroupRequestSchema>;
export type CreateGroupErrorCode = z.infer<typeof CreateGroupErrorCodeSchema>;
export type CreateGroupSuccessResponse = z.infer<typeof CreateGroupSuccessResponseSchema>;
export type CreateGroupErrorResponse = z.infer<typeof CreateGroupErrorResponseSchema>;
export type CreateGroupResponse = z.infer<typeof CreateGroupResponseSchema>;

// GET /group/:groupId

export const GroupParticipantSchema = z.object({
  id: GroupParticipantIdSchema,
  name: GroupParticipantNameSchema,
  matchViewedAt: z.iso.datetime().nullable(),
});

export const GroupSchema = z.object({
  id: GroupIdSchema,
  name: GroupNameSchema,
  participants: z.array(GroupParticipantSchema),
  createdAt: z.iso.datetime(),
});

export const GetGroupRequestSchema = z.object({
  groupId: GroupIdSchema,
});

export const GetGroupSuccessKinds = {
  ACTIVE: 'ACTIVE',
  DELETED: 'DELETED',
} as const;
export const GetGroupSuccessKindSchema = z.enum(GetGroupSuccessKinds);

export const GetGroupActiveResponseSchema = z.object({
  success: z.literal(true),
  kind: z.literal(GetGroupSuccessKinds.ACTIVE),
  group: GroupSchema,
});

export const DeletedGroupResponseSchema = z.object({
  success: z.literal(true),
  kind: z.literal(GetGroupSuccessKinds.DELETED),
  deletedAt: z.iso.datetime(),
});

export const GetGroupSuccessResponseSchema = z.discriminatedUnion('kind', [
  GetGroupActiveResponseSchema,
  DeletedGroupResponseSchema,
]);

export const GetGroupErrorCodes = {
  INVALID_INPUT: 'INVALID_INPUT',
  NOT_FOUND: 'NOT_FOUND',
} as const;
export const GetGroupErrorCodeSchema = z.enum(GetGroupErrorCodes);
export const GetGroupErrorResponseSchema = z.object({
  success: z.literal(false),
  code: GetGroupErrorCodeSchema,
});

export const GetGroupResponseSchema = z.discriminatedUnion('success', [
  GetGroupSuccessResponseSchema,
  GetGroupErrorResponseSchema,
]);

export type GroupParticipant = z.infer<typeof GroupParticipantSchema>;
export type Group = z.infer<typeof GroupSchema>;
export type GetGroupRequest = z.infer<typeof GetGroupRequestSchema>;
export type GetGroupSuccessKind = z.infer<typeof GetGroupSuccessKindSchema>;
export type DeletedGroupResponse = z.infer<typeof DeletedGroupResponseSchema>;
export type GetGroupErrorCode = z.infer<typeof GetGroupErrorCodeSchema>;
export type GetGroupActiveResponse = z.infer<typeof GetGroupActiveResponseSchema>;
export type GetGroupSuccessResponse = z.infer<typeof GetGroupSuccessResponseSchema>;
export type GetGroupErrorResponse = z.infer<typeof GetGroupErrorResponseSchema>;
export type GetGroupResponse = z.infer<typeof GetGroupResponseSchema>;

// GET /group/:groupId/participant/:participantId

export const GetGroupForParticipantRequestSchema = z.object({
  groupId: GroupIdSchema,
  participantId: GroupParticipantIdSchema,
});

export const GroupParticipantMatchSchema = z.object({
  id: GroupParticipantIdSchema,
  name: GroupParticipantNameSchema,
});

export const GetGroupForParticipantActiveResponseSchema = z.object({
  success: z.literal(true),
  kind: z.literal(GetGroupSuccessKinds.ACTIVE),
  group: GroupSchema,
  participant: GroupParticipantSchema,
  match: GroupParticipantMatchSchema,
  ownWishlist: z.array(WishlistItemSchema),
  matchWishlist: z.array(MatchWishlistItemSchema),
});

export const GetGroupForParticipantSuccessResponseSchema = z.discriminatedUnion('kind', [
  GetGroupForParticipantActiveResponseSchema,
  DeletedGroupResponseSchema,
]);

export const GetGroupForParticipantErrorCodes = {
  INVALID_INPUT: 'INVALID_INPUT',
  NOT_FOUND: 'NOT_FOUND',
} as const;
export const GetGroupForParticipantErrorCodeSchema = z.enum(GetGroupForParticipantErrorCodes);
export const GetGroupForParticipantErrorResponseSchema = z.object({
  success: z.literal(false),
  code: GetGroupForParticipantErrorCodeSchema,
});

export const GetGroupForParticipantResponseSchema = z.discriminatedUnion('success', [
  GetGroupForParticipantSuccessResponseSchema,
  GetGroupForParticipantErrorResponseSchema,
]);

export type GroupParticipantMatch = z.infer<typeof GroupParticipantMatchSchema>;
export type GetGroupForParticipantRequest = z.infer<typeof GetGroupForParticipantRequestSchema>;
export type GetGroupForParticipantErrorCode = z.infer<typeof GetGroupForParticipantErrorCodeSchema>;
export type GetGroupForParticipantActiveResponse = z.infer<
  typeof GetGroupForParticipantActiveResponseSchema
>;
export type GetGroupForParticipantSuccessResponse = z.infer<
  typeof GetGroupForParticipantSuccessResponseSchema
>;
export type GetGroupForParticipantErrorResponse = z.infer<
  typeof GetGroupForParticipantErrorResponseSchema
>;
export type GetGroupForParticipantResponse = z.infer<typeof GetGroupForParticipantResponseSchema>;

// POST /group/recordmatchview

export const RecordMatchViewRequestSchema = z.object({
  groupId: GroupIdSchema,
  participantId: GroupParticipantIdSchema,
});

export const RecordMatchViewSuccessResponseSchema = z.object({
  success: z.literal(true),
  groupId: GroupIdSchema,
  participantId: GroupParticipantIdSchema,
  matchViewedAt: z.iso.datetime(),
});

export const RecordMatchViewErrorCodes = {
  INVALID_INPUT: 'INVALID_INPUT',
  NOT_FOUND: 'NOT_FOUND',
} as const;
export const RecordMatchViewErrorCodeSchema = z.enum(RecordMatchViewErrorCodes);
export const RecordMatchViewErrorResponseSchema = z.object({
  success: z.literal(false),
  code: RecordMatchViewErrorCodeSchema,
});

export const RecordMatchViewResponseSchema = z.discriminatedUnion('success', [
  RecordMatchViewSuccessResponseSchema,
  RecordMatchViewErrorResponseSchema,
]);

export type RecordMatchViewRequest = z.infer<typeof RecordMatchViewRequestSchema>;
export type RecordMatchViewErrorCode = z.infer<typeof RecordMatchViewErrorCodeSchema>;
export type RecordMatchViewSuccessResponse = z.infer<typeof RecordMatchViewSuccessResponseSchema>;
export type RecordMatchViewErrorResponse = z.infer<typeof RecordMatchViewErrorResponseSchema>;
export type RecordMatchViewResponse = z.infer<typeof RecordMatchViewResponseSchema>;
